package or.garden.manager.domain.model

import gardenmanager.composeapp.generated.resources.Res
import gardenmanager.composeapp.generated.resources.basil
import gardenmanager.composeapp.generated.resources.beetroot
import gardenmanager.composeapp.generated.resources.board_beans
import gardenmanager.composeapp.generated.resources.broccoli
import gardenmanager.composeapp.generated.resources.cabbage
import gardenmanager.composeapp.generated.resources.calendula
import gardenmanager.composeapp.generated.resources.carrot
import gardenmanager.composeapp.generated.resources.cauliflower
import gardenmanager.composeapp.generated.resources.celery
import gardenmanager.composeapp.generated.resources.chard
import gardenmanager.composeapp.generated.resources.cherry_tomatoes
import gardenmanager.composeapp.generated.resources.chives
import gardenmanager.composeapp.generated.resources.coriander
import gardenmanager.composeapp.generated.resources.corn
import gardenmanager.composeapp.generated.resources.courgette
import gardenmanager.composeapp.generated.resources.cucumber
import gardenmanager.composeapp.generated.resources.eggplants
import gardenmanager.composeapp.generated.resources.garlic
import gardenmanager.composeapp.generated.resources.ic_seedling
import gardenmanager.composeapp.generated.resources.kale
import gardenmanager.composeapp.generated.resources.leek
import gardenmanager.composeapp.generated.resources.lettuce
import gardenmanager.composeapp.generated.resources.marigold
import gardenmanager.composeapp.generated.resources.okra
import gardenmanager.composeapp.generated.resources.onion
import gardenmanager.composeapp.generated.resources.parsley
import gardenmanager.composeapp.generated.resources.pea
import gardenmanager.composeapp.generated.resources.pepper
import gardenmanager.composeapp.generated.resources.potato
import gardenmanager.composeapp.generated.resources.pumpkin
import gardenmanager.composeapp.generated.resources.radish
import gardenmanager.composeapp.generated.resources.spinach
import gardenmanager.composeapp.generated.resources.strawberry
import gardenmanager.composeapp.generated.resources.sunflower
import gardenmanager.composeapp.generated.resources.sweet_potato
import gardenmanager.composeapp.generated.resources.tomato
import gardenmanager.composeapp.generated.resources.turnip
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import org.jetbrains.compose.resources.DrawableResource


@Serializable
data class CropData(
    val id: Int,
    val foilId: Int? = null,
    val uuid: String? = null,
    val name: String,
    val latinName: String? = null,
    val description: String? = null,
    val icon: String? = null,
    val spacingBetweenPlants: Int? = null,
    val spacingBetweenRows: Int? = null,
    val height: Int? = null,
    val resourceConsumption: String? = null,
    val waterRequirements: String? = null,
    // companions as full crop summaries (excluding their companions to prevent recursion)
    val goodCompanions: List<CropSummary> = emptyList(),
    val badCompanions: List<CropSummary> = emptyList(),
    val nutrients: List<NutrientStage>? = null,
    val frostSensitivity: String? = null,
    val minTemperature: Int? = null,
    val optimalTemperatureMin: Int? = null,
    val optimalTemperatureMax: Int? = null,
    val transplantSchedule: TransplantSchedule? = null,
    val isFavourite: Boolean = false,
    @SerialName("companion")
    val isCompanion: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val holeSpacing: Int? = null,
    val rows: Int? = null,
) {
    fun spacingBetweenPlants(bed: BedData): Int {
        if (bed.orientation == "0") return spacingBetweenPlants ?: 20
        return spacingBetweenRows ?: 20
    }

    fun spacingBetweenRows(bed: BedData): Int {
        if (bed.orientation == "0") return spacingBetweenRows ?: 20
        return spacingBetweenPlants ?: 20
    }

    fun spacing(): String {
        val w = spacingBetweenPlants ?: 0
        val l = spacingBetweenRows ?: 0
        return if (w <= 0 && l <= 0) "" else "${w} x ${l} cm"
    }

    fun favourite(): Boolean {
        return isFavourite
    }

    fun getDrawable(): DrawableResource {
        val key = icon?.lowercase() ?: ""
        return when (key) {
            "tomato" -> Res.drawable.tomato
            "cucumber" -> Res.drawable.cucumber
            "courgette", "zucchini" -> Res.drawable.courgette
            "lettuce" -> Res.drawable.lettuce
            "onion" -> Res.drawable.onion
            "potato" -> Res.drawable.potato
            "carrot" -> Res.drawable.carrot
            "celery" -> Res.drawable.celery
            "beetroot" -> Res.drawable.beetroot
            "cabbage" -> Res.drawable.cabbage
            "broccoli" -> Res.drawable.broccoli
            "pea" -> Res.drawable.pea
            "radish" -> Res.drawable.radish
            "chard" -> Res.drawable.chard
            "parsley" -> Res.drawable.parsley
            "cauliflower" -> Res.drawable.cauliflower
            "corn" -> Res.drawable.corn
            "turnip" -> Res.drawable.turnip
            "leek" -> Res.drawable.leek
            "cherry_tomatoes" -> Res.drawable.cherry_tomatoes
            "pepper" -> Res.drawable.pepper
            "eggplant" -> Res.drawable.eggplants
            "beans", "board_beans" -> Res.drawable.board_beans
            "spinach" -> Res.drawable.spinach
            "strawberry" -> Res.drawable.strawberry

            "basil" -> Res.drawable.basil
            "marigold" -> Res.drawable.marigold
            "calendula" -> Res.drawable.calendula
            "sunflower" -> Res.drawable.sunflower
            "chives" -> Res.drawable.chives

            "coriander" -> Res.drawable.coriander
            "pumpkin" -> Res.drawable.pumpkin
            "garlic" -> Res.drawable.garlic
            "kale" -> Res.drawable.kale
            "okra" -> Res.drawable.okra
            "sweet_potato" -> Res.drawable.sweet_potato
            else -> Res.drawable.ic_seedling
        }
    }
}

@Serializable
data class AddFavoriteRequest(
    val cropId: Int
)

@Serializable
data class NutrientStage(
    val weekStart: Int?,
    val weekEnd: Int? = null,
    val phaseName: String?,
    val nitrogen: String?,
    val phosphorus: String?,
    val potassium: String?
)

@Serializable
data class TransplantWindow(
    val startMonth: Int?,
    val endMonth: Int?,
    val startMonthName: String?,
    val endMonthName: String?,
    val notes: String? = null,
    val notesBS: String? = null,
    val notesEN: String? = null
)

@Serializable
data class TransplantSchedule(
    val mediterranean: TransplantWindow? = null,
    val continental: TransplantWindow? = null,
    val mountain: TransplantWindow? = null
)

@Serializable
data class Nutrients(
    val transplant: NutrientStage? = null,
    val flowering: NutrientStage? = null,
    val fruiting: NutrientStage? = null
)

@Serializable
data class CropSummary(
    val id: Int,
    val uuid: String? = null,
    val name: String,
    val latinName: String? = null,
    val description: String? = null,
    val icon: String? = null,
    val spacingBetweenPlants: Int? = null,
    val spacingBetweenRows: Int? = null,
    val height: Int? = null,
    val resourceConsumption: String? = null,
    val waterRequirements: String? = null,
    val nutrients: List<NutrientStage>? = null,
    val frostSensitivity: String? = null,
    val minTemperature: Int? = null,
    val optimalTemperatureMin: Int? = null,
    val optimalTemperatureMax: Int? = null,
    val transplantSchedule: TransplantSchedule? = null,
    val isFavourite: Boolean = false,
    @SerialName("companion")
    val isCompanion: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null
) {
    fun getDrawable(): DrawableResource {
        val key = icon?.lowercase() ?: ""
        return when (key) {
            "tomato" -> Res.drawable.tomato
            "cucumber" -> Res.drawable.cucumber
            "courgette", "zucchini" -> Res.drawable.courgette
            "lettuce" -> Res.drawable.lettuce
            "onion" -> Res.drawable.onion
            "potato" -> Res.drawable.potato
            "carrot" -> Res.drawable.carrot
            "celery" -> Res.drawable.celery
            "beetroot" -> Res.drawable.beetroot
            "cabbage" -> Res.drawable.cabbage
            "broccoli" -> Res.drawable.broccoli
            "pea" -> Res.drawable.pea
            "radish" -> Res.drawable.radish
            "chard" -> Res.drawable.chard
            "parsley" -> Res.drawable.parsley
            "cauliflower" -> Res.drawable.cauliflower
            "corn" -> Res.drawable.corn
            "turnip" -> Res.drawable.turnip
            "leek" -> Res.drawable.leek
            "cherry_tomatoes" -> Res.drawable.cherry_tomatoes
            "pepper" -> Res.drawable.pepper
            "eggplant" -> Res.drawable.eggplants
            "beans", "board_beans" -> Res.drawable.board_beans
            "spinach" -> Res.drawable.spinach
            "strawberry" -> Res.drawable.strawberry

            "basil" -> Res.drawable.basil
            "marigold" -> Res.drawable.marigold
            "calendula" -> Res.drawable.calendula
            "sunflower" -> Res.drawable.sunflower
            "chives" -> Res.drawable.chives

            "coriander" -> Res.drawable.coriander
            "pumpkin" -> Res.drawable.pumpkin
            "garlic" -> Res.drawable.garlic
            "kale" -> Res.drawable.kale
            "okra" -> Res.drawable.okra
            "sweet_potato" -> Res.drawable.sweet_potato
            else -> Res.drawable.ic_seedling
        }
    }
}

@Serializable
data class CropTranslation(
    val languageCode: String,
    val name: String,
    val latinName: String? = null,
    val description: String? = null
)

@Serializable
data class CropWithTranslations(
    val id: Int,
    val uuid: String,
    val icon: String? = null,
    val spacingBetweenPlants: Int = 20,
    val spacingBetweenRows: Int = 20,
    val height: Int? = null,
    val resourceConsumption: String? = null,
    val waterRequirements: String? = null,
    val goodCompanions: List<CropSummary> = emptyList(),
    val badCompanions: List<CropSummary> = emptyList(),
    val nutrients: List<NutrientStage>? = null,
    val frostSensitivity: String? = null,
    val minTemperature: Int? = null,
    val optimalTemperatureMin: Int? = null,
    val optimalTemperatureMax: Int? = null,
    val transplantSchedule: Map<String, TransplantWindow> = emptyMap(),
    val translations: List<CropTranslation>,
    @SerialName("companion")
    val isCompanion: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null
) {
    fun nutrients(): List<NutrientStage> {
        return listOf(
            nutrients?.find { it.phaseName == "Transplanting" } ?: NutrientStage(
                0,
                null,
                "Transplanting",
                null,
                null,
                null
            ),
            nutrients?.find { it.phaseName == "Flowering" } ?: NutrientStage(
                null,
                null,
                "Flowering",
                null,
                null,
                null
            ),
            nutrients?.find { it.phaseName == "Fruiting" } ?: NutrientStage(
                null,
                null,
                "Fruiting",
                null,
                null,
                null
            )
        )
    }
}

@Serializable
data class CropTranslationRequest(
    val languageCode: String,
    val name: String,
    val latinName: String? = null,
    val description: String? = null
)

@Serializable
data class CropRequest(
    val translations: List<CropTranslationRequest>,
    val icon: String? = null,
    val spacingBetweenPlants: Int? = null,
    val spacingBetweenRows: Int? = null,
    val height: Int? = null,
    val resourceConsumption: String? = null,
    val waterRequirements: String? = null,
    val frostSensitivity: String? = null,
    val minTemperature: Int? = null,
    val optimalTemperatureMin: Int? = null,
    val optimalTemperatureMax: Int? = null,
    val isCompanion: Boolean = false,
    val goodCompanionIds: List<Int> = emptyList(),
    val badCompanionIds: List<Int> = emptyList(),
    val nutrients: List<NutrientStage> = emptyList(),
    val transplantSchedule: Map<String, TransplantWindow> = emptyMap()
)
